<template>
  <div class="container">
    <div
      v-check-visible-viewport="onVisibleInViewport"
      :class="bucket && bucket.media_size ? `podcast-list-${bucket.media_size}` : ''"
      class="podcast-list"
      data-test="list-podcast"
    >
      <el-row
        v-if="bucket.name"
        :class="`list-header${!hasHoverState ? ' with-margin' : ''}`"
        type="flex"
        justify="space-between"
        align="middle"
        data-test="titlebucket">
        <h4
          class="heading"
        >
          {{ bucket.name }}
        </h4>
        <p
          v-button-event="{buttonText: 'see all podcasts', buttonLink: 'search_results_shows'}"
          v-if="see_all"
          @click="gotoPage(see_all_link)">
          SEE ALL <i class="el-icon-caret-right el-icon-right"/>
        </p>
      </el-row>
      <flickity
        v-if="!isStacked"
        ref="flickity"
        :options="flickityOptions"
        :key="bucketItems.length">
        <div
          v-for="(podcast, index) in bucketItems"
          :key="index"
          :data-segment-uuid="podcast.uuid"
          class="carousel-container">
          <podcast-card
            :redirect="redirect"
            :podcast="podcast"
            :publisher-name="podcast.publisher_name || ''"
            :type="podcast.item_type || podcast.type"
            :bucket="bucketData"
            :media-size="bucket && bucket.media_size ? bucket.media_size : ''"
            :key="index"
            :first-item="index === 0"
            :has-hover-state="hasHoverState"
            data-test="podcast-card"
            @onSelect="onSelectionToggle"/>
        </div>
      </flickity>
      <flickity
        v-if="isStacked"
        ref="flickity"
        :options="flickityOptions"
        :key="stackedBucketItems.length">
        <div
          v-for="(podcastSet, index) in stackedBucketItems"
          :key="index"
          class="carousel-container">
          <stacked-podcast-card
            :redirect="redirect"
            :podcasts="podcastSet"
            :bucket="bucketData"
            :media-size="bucket && bucket.media_size ? bucket.media_size : ''"
            :key="index"
            :first-item="index === 0"
            :has-hover-state="hasHoverState"
            :selection-toggle="onSelectionToggle"
            data-test="podcast-card"/>
        </div>
      </flickity>
    </div>
  </div>
</template>
<script>
import * as log from 'loglevel';
import Flickity from 'vue-flickity';
import PodcastCard from './PodcastCard';
import StackedPodcastCard from './StackedPodcastCard';
import CarouselMixin from '../shared/carouselMixin';

export default {
  components: {
    Flickity,
    'podcast-card': PodcastCard,
    'stacked-podcast-card': StackedPodcastCard,
  },
  mixins: [CarouselMixin],
  props: {
    hasHoverState: {
      type: Boolean,
      default: false,
    },
    podcast_offset: {
      type: Number,
      default: 0,
    },
    current_page: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    publisher_uuid: {
      type: String,
      default: '',
    },
    see_all: {
      type: Boolean,
      default: false,
    },
    see_all_link: {
      type: String,
      default: '',
    },
    isStacked: {
      type: Boolean,
      default: false,
    },
    bucket: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      flickityOptions: {
        prevNextButtons: true,
        freeScroll: true,
        pageDots: false,
        wrapAround: false,
        cellAlign: 'left',
        initialIndex: 0,
        contain: true,
        draggable: '>1',
        groupCells: true,
      },
      redirect: true,
      arrow_hidden: false,
      drag_end: false,
      scrollCount: 1,
      prevIndex: 0,
      itemsShown: 0,
      scroll_sensitivity: 500, // more means less sensitive
    };
  },
  computed: {
    stackedBucketItems() {
      return this.bucketItems.map((item, idx, arr) => {
        if (idx % 2 === 0) {
          const items = [item];
          if (arr[idx + 1]) items.push(arr[idx + 1]);
          return items;
        }
        return null;
      }).filter(item => item);
    },
  },
  watch: {
    bucketItems() {
      if (this.bucketItems.length) {
        this.reloadCells();
      }
    },
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.removeResizeEvent();
    this.enableVerticalScrolling();
  },
  methods: {
    init() {
      if (this.bucket.items && this.bucket.items.length) {
        this.bucketItems = this.bucket.items;
        this.bucketData = { ...this.bucket };
        this.bucketData.items = [];
        this.bucketData.position = this.$vnode.key;
        this.bucketData.items_count = this.bucket.items.length;
        this.dragStart();
        this.carouselSettled();
        this.arrowVisibility();
      }
    },
    reloadCells() {
      this.$refs.flickity.reloadCells();
      this.$forceUpdate();
    },
    disableVerticalScrolling() {
      if (!window || !window.isTouch || !this.isBrowserSafari()) return;
      // Disable vertical scrolling during horizontal swipe. Introduced for safari.
      window.addEventListener('touchstart', this.touchStart);
      window.addEventListener('touchmove', this.preventTouch, { passive: false });
    },
    enableVerticalScrolling() {
      window.removeEventListener('touchstart', this.touchStart);
      window.removeEventListener('touchmove', this.preventTouch);
    },
    touchStart(e) {
      this.firstClientX = e.touches[0].clientX;
      this.firstClientY = e.touches[0].clientY;
    },
    /* eslint-disable */
    preventTouch(e) {
      const minValue = 5; // threshold

      this.clientX = e.touches[0].clientX - this.firstClientX;
      this.clientY = e.touches[0].clientY - this.firstClientY;

      // Vertical scrolling does not work when you start swiping horizontally.
      if (Math.abs(this.clientX) > minValue) {
        e.preventDefault();
        e.returnValue = false;
        return false;
      }
    },
    async dragStart() {
      await this.$nextTick().catch((err) => { log.warn(err.message); });
      let ctx = this;
      this.$refs.flickity.on('dragStart', () => {
        this.drag_end = false;
        this.redirect = false;
      });
    },
    async dragEnd() {
      await this.$nextTick().catch((err) => { log.warn(err.message); });
      let ctx = this;
      this.$refs.flickity.on('dragEnd', () => {
        this.drag_end = true;
      });
    },
    async carouselSettled() {
      await this.$nextTick().catch((err) => { log.warn(err.message); });
      this.$refs.flickity.on('settle', (currIndex) => {
        this.triggerSegmentContentViewedEvent();
        this.scrollCount = currIndex + 1;
        this.redirect = true;
      });
    },
    async scrollPodcasts() {
      await this.$nextTick().catch((err) => { log.warn(err.message); });
      this.$refs.flickity.on('scroll', (event) => {
        log.info(event)
        if (event >= 0.9) {
          this.$emit('scroll-right');
        }
      });
    },
    mouseScroll(e) {
      if (Math.abs(e.wheelDeltaY)) {
        e.stopPropagation();
        return;
      }
      if (e.wheelDeltaX > 0) {
        const selectedCell = (this.itemsShown * (this.scrollCount - 1)) - 1;
        this.$refs.flickity.selectCell(selectedCell);
      } else {
        const selectedCell = (this.itemsShown * this.scrollCount) + 1;
        this.$refs.flickity.selectCell(selectedCell);
      }
    },
    bindMouseWheelEvent() {
      if (this.$refs.flickity) {
        this.$refs.flickity.$el.addEventListener('wheel', this.mouseScroll);
      } else {
        log.info('Flickity Not Yet Rendered');
      }
    },
    removeMouseWheelEvent() {
      if (this.$refs.flickity) {
        this.$refs.flickity.$el.removeEventListener('wheel', this.mouseScroll);
      } else {
        log.info('Flickity event does not exists');
      }
    },
    arrowVisibility() {
      this.checkWindowSizes();
      window.addEventListener('resize', this.checkWindowSizes);
    },
    removeResizeEvent() {
      window.removeEventListener('resize', this.checkWindowSizes);
    },
    checkWindowSizes() {
      if (!this.$refs.flickity) return;
      this.$nextTick()
        .then(() => {
          const pl = this.$el.querySelector('.podcast-list');
          const pc = this.$el.querySelector('.podcast-card');
          if (!pl || !pc) return;
          const a = pl.clientWidth;
          const b = pc.clientWidth;
          const c = Math.floor(a / b);
          this.itemsShown = c;
          if (this.bucketItems.length <= c) {
            this.hideArrows();
            this.removeMouseWheelEvent();
          } else {
            this.showArrows();
            this.bindMouseWheelEvent();
          }
        })
        .catch(err => log.info(err.message));
    },
    hideArrows() {
      const a = this.$el.querySelector('.flickity-button.next');
      const b = this.$el.querySelector('.flickity-button.previous');
      if (a) this.$el.querySelector('.flickity-button.next').className += ' d-none';
      if (b) this.$el.querySelector('.flickity-button.previous').className += ' d-none';
    },
    showArrows() {
      const elem = this.$el.querySelectorAll('.flickity-button.next,.flickity-button.previous');
      if (!elem[0] || !elem[1]) return;
      if (elem[0].classList.contains('d-none')) {
        this.$el.querySelector('.flickity-button.next').classList.remove('d-none');
      }
      if (elem[1].classList.contains('d-none')) {
        this.$el.querySelector('.flickity-button.previous').classList.remove('d-none');
      }
    },
    onSelectionToggle(podcast) {
      this.$emit('onSelect', podcast);
    },
    gotoPage(link) {
      this.$emit('moreClicked');
      if (link) this.$router.push(link);
    },
  },
};
</script>
<style lang="scss">
@import "../assets/scss/variables.scss";
.podcast-list {
  .list-header {
    // margin-top:56px;
    p {
      font-size: 13px;
      font-family: Favorit-light;
      font-weight: 600;
      color: $blue;
      cursor: pointer;
      }
    h4 {
      margin: 0 0 !important;
    }

    &.with-margin {
      margin-bottom: 16px;
    }
  }
}
@media(max-width: $sm) {
  .podcast-list {
    .list-header {
      margin-bottom: 8px;
    }
  }
  .flickity-button {
    display: none !important;
  }
}
</style>



// WEBPACK FOOTER //
// app/src/components/PodcastList.vue